import { emitFakeLiveUpdateEvent } from "@jugl-web/domain-resources/live-updates";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { taskTimeLogListUpdate$ } from "./useTaskTimeLogList";

/**
 * Centralized task time-log actions.
 *
 * Wraps RTK Query mutations (create, update, delete) and emits
 * extra events on success so all time-log lists and task components
 * stay synchronized.
 *
 * IMPORTANT: Always use this hook over calling mutations directly!
 */
export const useTimeLogActions = () => {
  const { tasksApi } = useRestApiProvider();

  const [createTimeLogMutation] = tasksApi.useCreateTaskTimeLogMutation();
  const [updateTimeLogMutation] = tasksApi.useUpdateTaskTimeLogMutation();
  const [deleteTimeLogMutation] = tasksApi.useDeleteTaskTimeLogMutation();

  const createTimeLog = async (
    params: Parameters<typeof createTimeLogMutation>[0]
  ) => {
    const createdTimeLog = await createTimeLogMutation(params).unwrap();

    const { taskId, entityId } = params;

    taskTimeLogListUpdate$.next({
      type: "time-log-created",
      taskId,
      timeLog: createdTimeLog,
    });

    emitFakeLiveUpdateEvent({
      action: "updated",
      action_by: "FAKE",
      data: {
        id: taskId,
        entity_id: entityId,
        time: new Date().toISOString(),
      },
      module: "task",
    });

    return createdTimeLog;
  };

  const updateTimeLog = async (
    params: Parameters<typeof updateTimeLogMutation>[0]
  ) => {
    const updatedTimeLog = await updateTimeLogMutation(params).unwrap();

    const { taskId, entityId } = params;

    taskTimeLogListUpdate$.next({
      type: "time-log-updated",
      taskId,
      timeLog: updatedTimeLog,
    });

    emitFakeLiveUpdateEvent({
      action: "updated",
      action_by: "FAKE",
      data: {
        id: taskId,
        entity_id: entityId,
        time: new Date().toISOString(),
      },
      module: "task",
    });

    return updatedTimeLog;
  };

  const deleteTimeLog = async (
    params: Parameters<typeof deleteTimeLogMutation>[0]
  ) => {
    await deleteTimeLogMutation(params).unwrap();

    const { taskId, entityId, timeLogId } = params;

    taskTimeLogListUpdate$.next({
      type: "time-log-deleted",
      taskId,
      timeLogId,
    });

    emitFakeLiveUpdateEvent({
      action: "updated",
      action_by: "FAKE",
      data: {
        id: taskId,
        entity_id: entityId,
        time: new Date().toISOString(),
      },
      module: "task",
    });
  };

  return {
    createTimeLog,
    updateTimeLog,
    deleteTimeLog,
  };
};
